import React, { useCallback, useState, useMemo } from 'react';
import noop from 'lodash/noop';
import moment from 'moment';
import { useAtomValue } from 'jotai';
import { enableDodAtom } from '../atoms';
import DatePickerWithOption from '../../summary-panel/SummaryContainer/DatePickerWithOptions';
import { calcComparisonInterval } from '../../summary-panel/SummaryContainer/utils/dates';
import {
  SummaryDatePicker,
  DatePickers as DatePickersStyled,
  ComparisonDisplay,
  ComparisonIntervals,
  ComparisonInterval,
  ComparisonHour,
  ComparisonDay,
  ComparisonYear,
  ComparisonTo,
  VsText,
} from './styled';

const DatePickers = ({
  interval,
  validateUpdateInterval,
  summaryDatePickerRef,
  durationAllowed,
}) => {
  const { from, to, shortcut } = interval;
  const [summaryInterval, setSummaryInterval] = useState([from, to, shortcut]);
  const dodEnabled = useAtomValue(enableDodAtom);

  // Calculate comparison interval for DOD display
  const comparisonInterval = useMemo(() => {
    if (!dodEnabled) return null;

    return calcComparisonInterval(
      {
        from: summaryInterval[0],
        to: summaryInterval[1],
        shortcut: summaryInterval[2],
      },
      durationAllowed,
    );
  }, [dodEnabled, summaryInterval, durationAllowed]);

  // Use styled-components for date picker styling

  const onCancel = useCallback(
    () => {
      if (summaryDatePickerRef.current) {
        setSummaryInterval([from, to, shortcut]);
        summaryDatePickerRef.current.reset();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [from, to, shortcut, summaryDatePickerRef.current],
  );

  const changeSummaryDatePicker = useCallback(
    ({ interval: updatedInterval, selectedShortcut }) => {
      setSummaryInterval([...updatedInterval, selectedShortcut]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onApply = useCallback(
    () => {
      validateUpdateInterval(
        [moment(summaryInterval[0]), moment(summaryInterval[1])],
        summaryInterval[2],
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [summaryInterval],
  );

  const renderComparisonInterval = (ts, withYear) => (
    <ComparisonInterval>
      <ComparisonHour>{ts.format('HH:mm')}</ComparisonHour>
      <div>
        <ComparisonDay>{ts.format('MMM DD')}</ComparisonDay>
        {withYear ? <ComparisonYear>{ts.format('YYYY')}</ComparisonYear> : null}
      </div>
    </ComparisonInterval>
  );

  return (
    <DatePickersStyled>
      {dodEnabled && comparisonInterval && comparisonInterval.from && comparisonInterval.to && (
        <>
          <SummaryDatePicker className="comparison-display">
            <ComparisonDisplay>
              <ComparisonIntervals>
                {renderComparisonInterval(moment(comparisonInterval.from), false)}
                <ComparisonTo>to</ComparisonTo>
                {renderComparisonInterval(moment(comparisonInterval.to), true)}
              </ComparisonIntervals>
            </ComparisonDisplay>
          </SummaryDatePicker>
          <VsText>vs</VsText>
        </>
      )}
      <SummaryDatePicker className={dodEnabled ? 'dod' : ''}>
        <DatePickerWithOption
          title="Time Frame"
          ref={summaryDatePickerRef}
          defaultValue={[moment(summaryInterval[0]), moment(summaryInterval[1])]}
          defaultShortcut={shortcut}
          onApply={onApply}
          onCancel={onCancel}
          onChange={changeSummaryDatePicker}
          showTimeInterval={false}
          durationAllowed={durationAllowed}
        />
      </SummaryDatePicker>
    </DatePickersStyled>
  );
};

DatePickers.defaultProps = {
  summaryDatePickerRef: {},
  validateUpdateInterval: noop,
  durationAllowed: true,
};

export default React.memo(DatePickers);
